import { useLocation } from "@/lib/router-compat";
import { getOgTypeForPath } from "@/lib/seo-routes";

type SchemaJson = Record<string, unknown> | Array<Record<string, unknown>>;

interface SEOHeadProps {
  /**
   * NOTE: title, description, and canonical are intentionally NOT rendered
   * here anymore. They are owned by the server-rendered route head
   * (src/lib/route-head.ts). Rendering them here too caused duplicate
   * <title>, <meta description>, <link canonical>, and og:/twitter: tags on
   * every page. Props are kept for backwards compatibility but ignored.
   *
   * React 19 hoists <meta> rendered anywhere in the tree into <head>,
   * including during SSR — no Helmet needed. Inline JSON-LD <script> blocks
   * render in place, which is valid for structured data.
   */
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  schemaJson?: SchemaJson;
}

const SEOHead = ({ ogImage, noIndex, schemaJson }: SEOHeadProps) => {
  const { pathname } = useLocation();
  const ogType = getOgTypeForPath(pathname);

  const schemas = schemaJson ? (Array.isArray(schemaJson) ? schemaJson : [schemaJson]) : [];

  return (
    <>
      {noIndex ? <meta name="robots" content="noindex,nofollow" /> : null}

      {/* og:type is per-route (article for editorial content, website otherwise).
          Default og:image / twitter:card / twitter:image live in the root route
          head so non-JS social crawlers always see them; only a page-specific
          override is emitted here. title/description/canonical/og:title/
          og:description/og:url are managed by routeHead. */}
      <meta property="og:type" content={ogType} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      {ogImage ? <meta property="og:image:secure_url" content={ogImage} /> : null}
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {schemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}
    </>
  );
};

export default SEOHead;
